import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon } from './Icon.js';
import { iconCheck, iconClose } from './generated/icons.js';
import { cx } from './util.js';

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  /** A filter chip is a toggle: it fills and shows a check when selected. */
  selected?: boolean;
  /** An input chip has a remove button. */
  onRemove?: () => void;
  removeLabel?: string;
}

/** Filter, assist and input chips. 40 dp high with a 48 dp hit area. */
export function Chip({
  children,
  selected,
  onRemove,
  removeLabel,
  className,
  type = 'button',
  ...rest
}: ChipProps) {
  return (
    <span className={cx('k-chip-wrap', className)}>
      <button
        {...rest}
        type={type}
        aria-pressed={selected}
        className={cx('k-chip k-layer k-t-label', selected && 'k-chip--selected')}
      >
        {selected ? <Icon icon={iconCheck} size={18} /> : null}
        {children}
      </button>
      {onRemove === undefined ? null : (
        <button
          type="button"
          className="k-chip__remove"
          aria-label={removeLabel ?? 'Remove'}
          onClick={onRemove}
        >
          <Icon icon={iconClose} size={16} />
        </button>
      )}
    </span>
  );
}

export interface SlotChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  selected: boolean;
  unavailable?: boolean;
}

/** A visit time. An unavailable time is disabled and says so in text, not by colour alone. */
export function SlotChip({
  children,
  selected,
  unavailable = false,
  className,
  type = 'button',
  ...rest
}: SlotChipProps) {
  return (
    <button
      {...rest}
      type={type}
      role="radio"
      aria-checked={selected}
      disabled={unavailable || rest.disabled}
      className={cx('k-chip k-slot k-layer k-t-label', selected && 'k-chip--selected', className)}
    >
      {selected ? <Icon icon={iconCheck} size={18} /> : null}
      {children}
    </button>
  );
}

/** Groups slot chips as one radio group with arrow key movement. */
export function SlotGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="k-slots"
      onKeyDown={(event) => {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
        if (!keys.includes(event.key)) return;
        const items = Array.from(
          event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)'),
        );
        const index = items.findIndex((item) => item === document.activeElement);
        if (index < 0) return;
        event.preventDefault();
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const next = items[(index + step + items.length) % items.length];
        next?.focus();
        next?.click();
      }}
    >
      {children}
    </div>
  );
}
